import styled from '@emotion/styled'
import { GetDoseDispensingProductsQueryDto } from '@pharmac-extranet/extranet-api'
import { BannerViewEnum } from '@pharmac-extranet/management-api'
import {
  ExtranetContainer,
  ExtranetExpandArrowColumn,
  ExtranetTable,
  ExtranetTableColumn,
  ExtranetTableColumnProps,
  ExtranetTableRow,
  NoResults,
  PharmacSearchWrapper,
  useBreakpoint,
  useDebounce,
  useIntersectionObserver,
  useLanguage,
} from '@pharmac-extranet/shared'
import { PharmacH4, PharmacIcon, PharmacLink, PharmacSearch, PharmacText } from '@pharmac/ui'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { createSearchParams, useSearchParams } from 'react-router-dom'
import { DoseDispensingCatalogFilters } from '../../components/dose-dispensing-catalog/DoseDispensingCatalogFilters'
import { DoseDispensingCatalogItem } from '../../components/dose-dispensing-catalog/DoseDispensingCatalogItem'
import { mapSearchParamsToQueryParams } from '../../components/dose-dispensing-catalog/util/query-parameter-mapping'
import {
  ExtranetCarousel,
  ExtranetCarouselImage,
  ExtranetCarouselItem,
} from '../../components/extranet/ExtranetCarousel'
import { ExtranetViewHeader } from '../../components/extranet/ExtranetViewHeader'
import { ExtranetWrapper } from '../../components/extranet/ExtranetWrapper'
import { ExtranetFilters } from '../../components/filters/ExtranetFilters'
import { NoResultsResponse } from '../../components/responses/NoResultsResponse'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { cleanQueryParams } from '../../features/infinite-query/util/cleanQueryParams'
import { getPages } from '../../features/infinite-query/util/getPages'
import { api } from '../../services/HttpService'

type SortColumnProps = Omit<ExtranetTableColumnProps, 'onClick'> & {
  sortKey: string
  onClick: (key: string) => void
}

const SortColumnRoot = styled(ExtranetTableColumn)`
  &:hover {
    cursor: pointer;
    text-decoration: underline;
    color: var(--color-primary);
  }
`

const SortColumnInner = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const SortColumnArrow = styled.div<{ isAsc: boolean }>`
  height: 100%;
  display: flex;
  align-items: center;

  margin-left: var(--spacing-medium);

  svg {
    transition: var(--transition-cubic-bezier);
    transform: rotate(${(p) => (p.isAsc ? '180deg' : '0deg')});
  }
`

const SortColumn = ({ sortKey, children, onClick, ...props }: SortColumnProps) => {
  const [searchParams] = useSearchParams()

  const key = searchParams.get('sortKey') ?? 'name'
  const order = searchParams.get('sortOrder') ?? 'asc'

  const isActive = key === sortKey

  return (
    <SortColumnRoot
      {...props}
      onClick={() => onClick(sortKey)}
    >
      <SortColumnInner>
        <div>{children}</div>
        {isActive && (
          <SortColumnArrow isAsc={order === 'asc'}>
            <PharmacIcon icon="chevronDown" />
          </SortColumnArrow>
        )}
      </SortColumnInner>
    </SortColumnRoot>
  )
}

export type GetDoseDispensingProductsParams = Omit<
  GetDoseDispensingProductsQueryDto,
  'cid' | 'pageSize'
>

export const DoseDispensingCatalogView = () => {
  const { t, key, i18n } = useLanguage(['extranet', 'common'])

  const { minDesktop, maxMobile } = useBreakpoint()

  const [searchParams, setSearchParams] = useSearchParams()
  const [searchInput, setSearchInput] = useState<string>('')
  const [debouncedParams, setDebouncedParams] = useDebounce({} as GetDoseDispensingProductsParams)

  const { customer } = useCustomerContext()

  useEffect(() => {
    if (searchInput !== debouncedParams.search) {
      setDebouncedParams((s) => ({ ...s, search: searchInput }))
    }
  }, [searchInput])

  const handleResetFilters = () => {
    setSearchInput('')
    setSearchParams(createSearchParams({ sortKey: 'name', sortOrder: 'asc' }))
  }

  const [params, setParams] = useState<GetDoseDispensingProductsParams>(
    mapSearchParamsToQueryParams(searchParams)
  )

  const fetchPage = (pageParam: number) => {
    return api.products.getDoseDispensingProducts({
      cid: customer?.id as number,
      pageSize: 40,
      ...params,
      page: pageParam,
    })
  }

  const { data, isLoading, isFetchingNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [
      '__doseDispensingCatalog',
      customer?.id,
      params.availableForCustomers,
      params.halving,
      params.search,
      params.sortKey,
      params.sortOrder,
      params.manufacturerName,
      params.type,
    ],
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
  })

  const [scrollRef] = useIntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      fetchNextPage()
    }
  })

  useEffect(() => {
    setParams(() => mapSearchParamsToQueryParams(searchParams))
  }, [searchParams])

  useEffect(() => {
    setSearchParams(
      createSearchParams(
        cleanQueryParams({
          ...params,
          ...debouncedParams,
        })
      )
    )
  }, [debouncedParams])

  const handleSort = (key: string) => {
    const isCurrentKey = (searchParams.get('sortKey') ?? 'name') === key
    const currentDirection = searchParams.get('sortOrder') ?? 'asc'

    const nextDirection = !isCurrentKey ? 'asc' : currentDirection === 'asc' ? 'desc' : 'asc'

    setSearchParams(
      createSearchParams(
        cleanQueryParams({
          ...params,
          sortOrder: nextDirection,
          sortKey: key,
        })
      )
    )
  }

  const checkIfFiltersAreActive = () => {
    const params = mapSearchParamsToQueryParams(searchParams)

    const hasOnlySortParams = Object.keys(params).length === 2
    const isDefaultSort = params.sortKey === 'name' && params.sortOrder === 'asc'

    return !hasOnlySortParams || !isDefaultSort
  }

  const products = getPages(data)

  const noResults = !isLoading && !isFetchingNextPage && products.length === 0

  const { data: bannersData } = useQuery({
    queryKey: ['__getBanners', i18n.language, BannerViewEnum.DispensingProducts],
    queryFn: async () =>
      await api.banners.getAllBanners({
        lang: i18n.language,
        view: BannerViewEnum.DispensingProducts,
      }),
  })

  return (
    <ExtranetWrapper>
      <ExtranetContainer
        full
        marginSmallTop={!maxMobile}
      >
        <ExtranetCarousel autoplay>
          {(bannersData || []).map((banner) => (
            <ExtranetCarouselItem key={banner.id}>
              <PharmacLink
                href={key(banner.url)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExtranetCarouselImage
                  imageSrc={!maxMobile ? key(banner.image) : key(banner.mobileImage)}
                  imageAlt={key(banner.url)}
                />
              </PharmacLink>
            </ExtranetCarouselItem>
          ))}
        </ExtranetCarousel>
      </ExtranetContainer>
      <ExtranetContainer
        full
        flexColumn
        paddingSmallHorizontal={!minDesktop}
      >
        <ExtranetContainer full>
          <ExtranetViewHeader title={t('medicines.title')}>
            <PharmacSearchWrapper>
              <PharmacSearch
                onChange={(v) => setSearchInput(v)}
                value={searchInput}
                placeholder={t('search', { ns: 'common' })}
                searchButtonContent={
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <PharmacIcon
                      icon="search"
                      size="22"
                    />
                  </div>
                }
              />
              <div style={{ width: 'var(--spacing-small)' }} />
              <ExtranetFilters
                activeFilterCount={Object.keys(params).length - 2}
                title={t('filters', { ns: 'common' })}
                resetFilters={handleResetFilters}
                hasActiveFilters={checkIfFiltersAreActive()}
                render={({ closeFilters }) => (
                  <DoseDispensingCatalogFilters closeFilters={closeFilters} />
                )}
              />
            </PharmacSearchWrapper>
          </ExtranetViewHeader>
        </ExtranetContainer>
        <ExtranetContainer full>
          <ExtranetTable style={{ fontFamily: 'var(--font-family-secondary)', fontWeight: 300 }}>
            <ExtranetTableRow
              style={{
                backgroundColor: 'var(--extranet-light-purple)',
                border: 'none',
                fontWeight: 500,
              }}
            >
              <ExtranetTableColumn
                size={1.4}
                flexJustify="center"
                textAlign="center"
                style={{
                  minWidth: 105,
                  maxWidth: 105,
                }}
              >
                {t('medicines.productNumber')}
              </ExtranetTableColumn>
              <SortColumn
                onClick={handleSort}
                sortKey="name"
                size={minDesktop ? 3 : 14.5}
                paddingMediumHorizontal
              >
                {t('medicines.productName')}
              </SortColumn>
              {minDesktop && (
                <>
                  <ExtranetTableColumn size={2}>{t('medicines.strength')}</ExtranetTableColumn>
                  <ExtranetTableColumn size={3.5}>
                    {t('medicines.activeSubstancesDescription')}
                  </ExtranetTableColumn>
                  <ExtranetTableColumn size={2}>
                    {t('medicines.manufacturerName')}
                  </ExtranetTableColumn>
                  <ExtranetTableColumn size={2}>
                    {t('medicines.availableForCustomers')}
                  </ExtranetTableColumn>
                  <ExtranetTableColumn size={2}>{t('medicines.halving')}</ExtranetTableColumn>
                </>
              )}
              <ExtranetExpandArrowColumn />
            </ExtranetTableRow>
            <React.Fragment>
              {!noResults &&
                products.map((product) => (
                  <DoseDispensingCatalogItem
                    handleSubstituteSearch={(v) => {
                      setSearchInput(v)
                      setSearchParams(createSearchParams({ sortKey: 'name', sortOrder: 'asc' }))
                    }}
                    key={product.id}
                    product={product}
                  />
                ))}
              {noResults && <NoResultsResponse />}
            </React.Fragment>
          </ExtranetTable>
        </ExtranetContainer>
        <div
          ref={scrollRef}
          style={{ height: 200 }}
        />
        {!isLoading && data?.pages.length === 0 && (
          <NoResults marginLargeTop>
            <PharmacIcon
              icon="frown"
              size={'50px'}
            />
            <PharmacH4 marginMediumTop>{t('noSearchResultsTitle', { ns: 'common' })}</PharmacH4>
            <PharmacText marginMediumTop>
              {t('noSearchResultsDescription', { ns: 'common' })}
            </PharmacText>
          </NoResults>
        )}
      </ExtranetContainer>
    </ExtranetWrapper>
  )
}
